import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { ChevronDown, Contrast, LogOut, ScrollText, Settings, Users } from "lucide-react";
import { ROLE_LABELS, type SessionResponse } from "@amber/access/contracts";
import { sessionQuery, signOut } from "@amber/access/ui";
import { BudgetBanner, PauseBanner, TasksBadge } from "@amber/operations/ui";
import { GlobalSearch } from "./search/GlobalSearch";
import {
  AppLink,
  Avatar,
  LinkButton,
  Mark,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  cx,
  formatTime,
  useAppNavigate,
  useCsrf,
} from "@amber/ui";
import { useTheme, type ThemeChoice } from "./theme";

const NAV = [
  { href: "/", label: "Home" },
  { href: "/backlog", label: "Backlog" },
  { href: "/sources", label: "Sources" },
  { href: "/profiles", label: "Profiles" },
  { href: "/tasks", label: "Tasks" },
] as const;

const STALE_AFTER_MS = 60_000;

function isCurrent(pathname: string, href: string): boolean {
  return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
}

export function AppShell({
  session,
  view,
  status,
  topbar,
  children,
}: {
  session: SessionResponse;
  view: string;
  status?: ReactNode;
  topbar?: ReactNode;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const inSettings = pathname.startsWith("/settings");
  return (
    <div className="flex h-full min-w-[320px] flex-col">
      <a
        href="#main"
        className="sr-only z-skip-link rounded-md bg-bg px-3 py-2 font-semibold text-link focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:shadow-pop"
      >
        Skip to content
      </a>
      <header className="flex h-topbar flex-none items-center gap-3 border-b border-divider bg-bg px-4 max-compact:px-2">
        <AppLink href="/" className="flex items-center gap-2 text-fg no-underline" aria-label="Amber, home">
          <Mark className="size-6" />
          <span className="font-semibold max-narrow:hidden">Amber</span>
        </AppLink>
        <span className="text-muted max-[1100px]:hidden">{view}</span>
        <nav aria-label="Main" className="ml-5 flex items-center gap-0.5 max-narrow:ml-1">
          {NAV.map((item) => (
            <AppLink
              key={item.href}
              href={item.href}
              aria-current={isCurrent(pathname, item.href) ? "page" : undefined}
              className="whitespace-nowrap rounded-lg px-2.5 py-1.5 font-strong text-fg no-underline hover:bg-surface-2 aria-[current=page]:bg-surface-3 max-narrow:px-1.5"
            >
              {item.label}
              {item.href === "/tasks" ? <TasksBadge /> : null}
            </AppLink>
          ))}
        </nav>
        <span className="flex-1" />
        <GlobalSearch />
        {topbar}
        <ThemeMenu />
        <AppLink
          href="/settings/users"
          aria-label="Settings"
          aria-current={inSettings ? "page" : undefined}
          className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-bg text-muted hover:bg-surface-2 hover:text-fg aria-[current=page]:bg-surface-3 aria-[current=page]:text-fg"
        >
          <Settings size={15} strokeWidth={1.8} aria-hidden="true" />
        </AppLink>
        <UserMenu session={session} />
      </header>
      <main id="main" tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">
        <PauseBanner />
        {session.user.role === "admin" ? <BudgetBanner /> : null}
        {children}
      </main>
      <StatusBar>{status}</StatusBar>
    </div>
  );
}

function ThemeMenu() {
  const [theme, setTheme] = useTheme();
  return (
    <MenuRoot>
      <MenuTrigger
        aria-label="Theme"
        className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-bg text-muted hover:bg-surface-2 hover:text-fg aria-expanded:bg-surface-3 aria-expanded:text-fg"
      >
        <Contrast size={15} strokeWidth={1.8} aria-hidden="true" />
      </MenuTrigger>
      <MenuContent className="w-[180px]">
        <MenuLabel>Theme</MenuLabel>
        <MenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (value === "system" || value === "light" || value === "dark") setTheme(value satisfies ThemeChoice);
          }}
        >
          <MenuRadioItem value="system">Follow system</MenuRadioItem>
          <MenuRadioItem value="light">Light</MenuRadioItem>
          <MenuRadioItem value="dark">Dark</MenuRadioItem>
        </MenuRadioGroup>
      </MenuContent>
    </MenuRoot>
  );
}

function UserMenu({ session }: { session: SessionResponse }) {
  const navigate = useAppNavigate();
  const client = useQueryClient();
  const csrf = useCsrf();
  const { user } = session;
  const leave = async () => {
    try {
      await signOut(csrf());
    } finally {
      client.clear();
      navigate("/sign-in?signed_out=1");
    }
  };
  return (
    <MenuRoot>
      <MenuTrigger
        aria-label={`Account: ${user.display_name}`}
        className="inline-flex h-8 items-center gap-1 rounded-full border border-divider bg-bg py-0.5 pr-1.5 pl-0.5 text-muted hover:bg-surface-2 aria-expanded:bg-surface-3"
      >
        <Avatar name={user.display_name} size="md" />
        <ChevronDown size={12} strokeWidth={1.8} aria-hidden="true" />
      </MenuTrigger>
      <MenuContent className="w-[280px]">
        <div className="px-2 pt-1 pb-2">
          <div className="font-semibold">{user.display_name}</div>
          <div className="text-md text-muted">{user.email}</div>
          <div className="mt-1 text-md">
            Role: <b className="font-strong">{ROLE_LABELS[user.role]}</b>
          </div>
          <div className="mt-0.5 text-sm text-muted">Signed in with Google Workspace · {user.domain}</div>
        </div>
        <MenuSeparator />
        <MenuItem
          onSelect={() => {
            navigate(`/settings/audit?actor=${encodeURIComponent(user.id)}`);
          }}
        >
          <ScrollText aria-hidden="true" />
          Your actions in the audit log
        </MenuItem>
        {user.role === "admin" ? (
          <MenuItem
            onSelect={() => {
              navigate("/settings/users");
            }}
          >
            <Users aria-hidden="true" />
            Users and roles
          </MenuItem>
        ) : null}
        <MenuSeparator />
        <MenuItem onSelect={() => void leave()}>
          <LogOut aria-hidden="true" />
          Sign out
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );
}

function StatusBar({ children }: { children?: ReactNode }) {
  const client = useQueryClient();
  const { dataUpdatedAt, isError } = useQuery(sessionQuery);
  const stale = isError || Date.now() - dataUpdatedAt > STALE_AFTER_MS;
  return (
    <footer className="flex h-statusbar flex-none items-center gap-4 overflow-hidden border-t border-divider bg-surface px-4 text-md whitespace-nowrap text-muted max-compact:px-2">
      {stale ? (
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-2 rounded-full border-[1.5px] border-muted" />
          Not live · last updated {formatTime(new Date(dataUpdatedAt))}
          <LinkButton
            onClick={() => {
              void client.invalidateQueries();
            }}
          >
            Retry
          </LinkButton>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={cx("size-2 rounded-full bg-done animate-live")} />
          Live · updates every 15 s
        </span>
      )}
      {children}
    </footer>
  );
}
